import { PeriodRangeString } from '../../../../store/types'
import { PagePaginationFromKeys, ServerResponse } from '../../../../types'
import {
  TraderProfitabilityMetrics,
  TraderProfitabilityTag,
} from '../../../../user/traderProfitability'
import { UserAccount, UserPlatform, UserRole, UserRolesEnum } from '../../../../user/types'

export { TraderProfitabilityTag }
export type { TraderProfitabilityMetrics }

export type UsersUserAccount = Omit<UserAccount, 'password' | 'name'> &
  TraderProfitabilityMetrics & {
    createdAt: string
  }

export enum UserColumnsEnum {
  firstName = 'firstName',
  lastName = 'lastName',
  role = 'role',
  country = 'country',
  email = 'email',
  phone = 'phone',
  isEmailVerified = 'isEmailVerified',
  isQuestionnaireCompleted = 'isQuestionnaireCompleted',
  googleAuth = 'googleAuth',
  appleAuth = 'appleAuth',
  registrationPlatform = 'registrationPlatform',
  usedPlatforms = 'usedPlatforms',
  lastActivePlatform = 'lastActivePlatform',
  createdAt = 'createdAt',
  lastLoginAt = 'lastLoginAt',
  lastActiveAt = 'lastActiveAt',
  profitabilityTag = 'profitabilityTag',
  profitabilityNetPnl30dUsd = 'profitabilityNetPnl30dUsd',
  userAccountPlatform = 'userAccountPlatform',
  userAccountCurrency = 'userAccountCurrency',
  userAccountCreatedAt = 'userAccountCreatedAt',
  userAccountBalance = 'userAccountBalance',
  userAccountLogin = 'userAccountLogin',
  userAccountServer = 'userAccountServer',
  userAccountManualBrokerName = 'userAccountManualBrokerName',
  userAccountImportType = 'userAccountImportType',
  userAccountCredentialsValid = 'userAccountCredentialsValid',
}

export type UsersColumnsType = Record<UserColumnsEnum, string>

export type UserColumnKeys = `${UserColumnsEnum}`

export enum UserFiltersEnum {
  hasAccounts = 'hasAccounts',
}

export type UserFiltersKeys = `${UserFiltersEnum}`

export enum UserAccountScopeEnum {
  all = 'all',
  withAccounts = 'withAccounts',
  withoutAccounts = 'withoutAccounts',
}

export type UserSignInType = 'email' | 'google' | 'apple'

export type UsersPagination = PagePaginationFromKeys<UserColumnKeys, UserFiltersEnum[]> & {
  accountScope: UserAccountScopeEnum
  roles: UserRole[]
  registrationPlatforms: UserPlatform[]
  signInTypes: UserSignInType[]
  emailVerified?: boolean
  questionnaireCompleted?: boolean
  profitabilityTags: TraderProfitabilityTag[]
  createdAtPeriod: PeriodRangeString
  lastLoginAtPeriod: PeriodRangeString
  lastActiveAtPeriod: PeriodRangeString
}

export type UsersUser = TraderProfitabilityMetrics & {
  id: string
  firstName: string
  lastName: string
  signInType?: UserSignInType
  role: UserRole
  country: string
  email: string
  phone: string
  isEmailVerified: boolean
  isQuestionnaireCompleted: boolean
  googleAuth: boolean
  appleAuth: boolean
  registrationPlatform?: string
  usedPlatforms?: string[]
  lastActivePlatform?: string
  createdAt: string
  lastLoginAt: string
  lastActiveAt: string
  userAccounts: UsersUserAccount[]
}

export type UsersData = {
  data: UsersUser[]
  meta: UsersPagination
}

export type GetUsersRequest = Pick<
  UsersPagination,
  | 'page'
  | 'perPage'
  | 'columns'
  | 'order'
  | 'orderBy'
  | 'search'
  | 'filters'
  | 'accountScope'
  | 'roles'
  | 'registrationPlatforms'
  | 'signInTypes'
  | 'emailVerified'
  | 'questionnaireCompleted'
  | 'profitabilityTags'
  | 'createdAtPeriod'
  | 'lastLoginAtPeriod'
  | 'lastActiveAtPeriod'
>
export type GetUsersResponse = ServerResponse<UsersData>

export const defaultUsersRoles: UserRole[] = [UserRolesEnum.USER, UserRolesEnum.ADMIN]
export const defaultUsersRegistrationPlatforms: UserPlatform[] = ['web', 'ios', 'android']
export const defaultUsersSignInTypes: UserSignInType[] = ['email', 'google', 'apple']
export const defaultTraderProfitabilityTags = Object.values(TraderProfitabilityTag)

export type GetUsersCSVResponse = Blob

export type UserSessionItem = {
  sessionId: string
  platform?: 'web' | 'ios' | 'android' | null
  ipAddress?: string | null
  userAgent?: string | null
  browserName?: string | null
  osName?: string | null
  deviceType?: string | null
  startedAt: string
  lastSeenAt: string
  endedAt?: string | null
  endReason?: string | null
}

export type GetUserSessionsRequest = {
  userId: number
  page?: number
  perPage?: number
  search?: string
  startedAtPeriod?: [string | null, string | null]
}

export type GetUserSessionsData = {
  data: UserSessionItem[]
  meta: {
    page: number
    perPage: number
    itemCount: number
    pageCount: number
    hasPreviousPage: boolean
    hasNextPage: boolean
  }
}

export type GetUserSessionsResponse = ServerResponse<GetUserSessionsData>
